/*
 * Authors: mariushirjanu
 * Created: 06/09/2022
 */
import _ from 'underscore';
import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { util } from '@app/shared/util';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { IAccessSettingsResponse, ICompanyAccessSettings, IWebshopAccessSettings, UserProfileDataService } from '../userprofile-data.service';

import { AuthService } from '@app/shared/services/auth/auth.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IUserInfo, USERPROFILE_WIZARD, UserProfileService } from '../user-profile.service';
import { PermissionsService } from './permissions.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import { STATE_REPORT } from '@app/shared/app-states';
import { IACSLPermission, ICustomerPermissionExtended, IOrderQuotePermissionResponse, IPermissionTypeACSL, IWebshopPermission, IWebshopPermissionResponse, IWebshopPermissionSelector } from '@app/shared/user/user-data.service';
import { ICompany, IContact, IGenericUser, IObject, IWebshop } from '@type_definitions/datatypes';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { EVENT_USER_PROFILE_UPDATED } from '@app/shared/app-events';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { IValidatingComponent, IValidationContext } from '@app/shared/formly/ga-validate.service';
import { UserProfileStepIds } from '../user-profile-steps';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { IDualMultiSelectElement } from '@app/shared/dual-multi-select/dual-multi-select.component';
import { URL_WEBSHOP_ACCESS_SETTINGS } from '@app/shared/browser/url-collections';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

interface IUserPermission {
  userInfo?:IUserInfo;
  acslPermissions?:IACSLPermission;
  customerPermissions?:ICustomerPermissionExtended;
  webshopPermissions?:IWebshopPermission;
  accessSettings?:IAccessSettings[];
}

interface IFormFields {
  userInfoFields?:IExtraFieldConfigurationObject[];
  webshopPermissionsFields?:IExtraFieldConfigurationObject[];
}

interface IAccessSettings {
  name:string;
  url:string;
  emailSuffix:string;
  userApproval:boolean;
  approver:IContact;
  formattedApprover:string;
  isMainWebshop?:boolean;
  webshopId?:number;
  companyId?:number;
  buttons:IButtonStatusOptions;
}

interface IButtonStatusOptions {
  addEmailSuffixBusy?:boolean;
  addEmailSuffixDisabled?:boolean;
  sendReminderBusy?:boolean;
  sendReminderDisabled?:boolean;
}

@Component({
  selector: 'user-permissions',
  template: `
    <div class="permissions">

      <div class="row block-max-width">
        <!-- Generic user -->
        <div class="col-md-12">
          <formly [form]="userInfoForm" [fields]="fields?.userInfoFields" [model]="model.userInfo" class="form-horizontal"></formly>
        </div>

        <!-- Access settings -->
        <div class="col-md-12">

          <h4 *ngIf="loadingAccessSettings || model.accessSettings" class="form-title">{{isCustomerUser? 'Webshop' : 'Company'}} Access Settings</h4>
          <p *ngIf="loadingAccessSettings">
            <activity-indicator></activity-indicator>
            <span>Loading {{isCustomerUser? 'webshop' : 'company'}} access settings…</span>
          </p>

          <table *ngIf="!loadingAccessSettings && model.accessSettings?.length" class="access-settings"> 

            <tbody *ngFor="let accessSettings of model.accessSettings; let last = last; trackById">
              <tr>
                <th>Name</th>
                <td [attr.colspan]="2">
                  <a [href]="accessSettings.url" target="_blank">{{accessSettings.name}}</a>
                </td>
              </tr>
              <tr *ngIf="accessSettings.isMainWebshop !== undefined">
                <th>Main webshop</th>
                <td [attr.colspan]="2">{{accessSettings.isMainWebshop? 'yes' : 'no'}}</td>
              </tr>
              <tr>
                <th>Email suffix</th>
                <td *ngIf="accessSettings.emailSuffix">{{accessSettings.emailSuffix}}</td>
                <td [attr.colspan]="accessSettings.emailSuffix ? '1' : '2'">
                  <button (click)="addEmailSuffix(accessSettings)" [busyButton]="accessSettings.buttons.addEmailSuffixBusy" [bbPreventDisable]="true" [disabled]="accessSettings.buttons.addEmailSuffixDisabled" class="btn btn-default btn-sm">
                    Add Email Suffix
                  </button>
                </td>
              </tr>
              <tr>
                <th>Approval needed</th>
                <td [attr.colspan]="2">{{accessSettings.userApproval? 'yes' : 'no'}}</td>
              </tr>
              <tr>
                <th>Approver</th>
                <td>{{accessSettings.formattedApprover}}</td>
                <td>
                  <button *ngIf="accessSettings.userApproval && registrationState === 'WAIT_APRVL'" (click)="sendReminderToApprover(accessSettings)" [busyButton]="accessSettings.buttons.sendReminderBusy" [bbPreventDisable]="true" [disabled]="accessSettings.buttons.sendReminderDisabled" class="btn btn-default btn-sm">
                    Send Reminder
                  </button>
                </td>
              </tr>
              <tr>
                <td *ngIf="!last" [attr.colspan]="3">&nbsp;</td>
              </tr>
            </tbody>

          </table>

          <div *ngIf="!loadingAccessSettings && model.accessSettings && !model.accessSettings.length">No {{isCustomerUser? 'webshop' : 'company'}} settings available</div>

        </div>


        <!-- Permissions -->
        <div class="col-md-12">

          <!-- Permissions view for ACSL users -->
          <div *ngIf="isACSLUser">

            <h4 *ngIf="loadingPermissions || model.acslPermissions?.order" class="form-title" translate>APP.USER_PROFILE.HEADINGS.ORDER_PERMISSIONS</h4>
            <p *ngIf="loadingPermissions">
              <activity-indicator></activity-indicator>
              <span translate>APP.USER_PROFILE.PERMISSIONS.LOADING_PERMISSONS</span>
            </p>

            <table *ngIf=" model.acslPermissions?.order" class="order-permissions">

              <thead *ngIf="model.acslPermissions.order.create.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.ORDERS_PLACE</span>:</th>
                </tr>
              </thead>

              <ng-container *ngIf="model.acslPermissions.order.create.length">
                <tbody *ngFor="let create of model.acslPermissions.order.create; let index = index; trackById">
                  <tr *ngFor="let location of create.location; trackById">
                    <td [attr.rowspan]="create.location.length" *ngIf="index == 0">{{create.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>

              <thead *ngIf="model.acslPermissions.order.viewOwn.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.ORDERS_VIEW_OWN</span>:</th>
                </tr>
              </thead>
              <ng-container *ngIf="model.acslPermissions.order.viewOwn">
                <tbody *ngFor="let viewOwn of model.acslPermissions.order.viewOwn; trackById">
                  <tr *ngFor="let location of viewOwn.location; let index = index; trackById">
                    <td [attr.rowspan]="viewOwn.location.length" *ngIf="index == 0">{{viewOwn.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>

              <thead *ngIf="model.acslPermissions.order.viewOthers.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.ORDERS_VIEW_OTHERS</span>:</th>
                </tr>
              </thead>
              <ng-container *ngIf="model.acslPermissions.order.viewOthers.length">
                <tbody *ngFor="let viewOthers of model.acslPermissions.order.viewOthers; trackById">
                  <tr *ngFor="let location of viewOthers.location; let index = index; trackById">
                    <td [attr.rowspan]="viewOthers.location.length" *ngIf="index == 0">{{viewOthers.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>
              <tbody *ngIf="allowToChangeCustomerPermissions">
                <tr>
                  <td [attr.colspan]="2" class="col-space-top">
                    <a uiSref="${STATE_REPORT}" [uiParams]="getReportPermissionsSREFParams" translate>APP.USER_PROFILE.PERMISSIONS.CHANGE_PERMISSIONS</a>
                  </td>
                </tr>
              </tbody>

            </table>


            <h4 *ngIf="loadingPermissions || model.acslPermissions?.quote" class="form-title" translate>APP.USER_PROFILE.HEADINGS.QUOTE_PERMISSIONS</h4>
            <p *ngIf="loadingPermissions">
              <activity-indicator></activity-indicator>
              <span translate>APP.USER_PROFILE.PERMISSIONS.LOADING_PERMISSONS</span>
            </p>

            <table *ngIf="model.acslPermissions?.quote" class="quote-permissions">

              <thead *ngIf="model.acslPermissions.quote.create.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.QUOTES_PLACE</span>:</th>
                </tr>
              </thead>
              <ng-container *ngIf="model.acslPermissions.quote.create.length">
                <tbody *ngFor="let create of model.acslPermissions.quote.create; trackById">
                  <tr *ngFor="let location of create.location; let index = index; trackById">
                    <td [attr.rowspan]="create.location.length" *ngIf="index == 0">{{create.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>

              <thead *ngIf="model.acslPermissions.quote.viewOwn.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.QUOTES_VIEW_OWN</span>:</th>
                </tr>
              </thead>
              <ng-container *ngIf="model.acslPermissions.quote.viewOwn.length">
                <tbody *ngFor="let viewOwn of model.acslPermissions.quote.viewOwn; trackById">
                  <tr *ngFor="let location of viewOwn.location; let index = index; trackById">
                    <td [attr.rowspan]="viewOwn.location.length" *ngIf="index == 0">{{viewOwn.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>
              <thead *ngIf="model.acslPermissions.quote.viewOthers.length">
                <tr>
                  <th [attr.colspan]="2"><span translate>APP.USER_PROFILE.PERMISSIONS.QUOTES_VIEW_OTHERS</span>:</th>
                </tr>
              </thead>
              <ng-container *ngIf="model.acslPermissions.quote.viewOthers.length">
                <tbody *ngFor="let viewOthers of model.acslPermissions.quote.viewOthers; trackById">
                  <tr *ngFor="let location of viewOthers.location; let index = index; trackById">
                    <td [attr.rowspan]="viewOthers.location.length" *ngIf="index == 0">{{viewOthers.country.name}}</td>
                    <td>{{location.name}}</td>
                  </tr>
                </tbody>
              </ng-container>
              <tbody *ngIf="allowToChangeCustomerPermissions">
                <tr>
                  <td [attr.colspan]="2" class="col-space-top">
                    <a uiSref="${STATE_REPORT}" [uiParams]="getReportPermissionsSREFParams" translate>APP.USER_PROFILE.PERMISSIONS.CHANGE_PERMISSIONS</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Permissions view for Customer users -->
          <div class="customer-permissions" *ngIf="isCustomerUser">
            <customer-permissions [customerPermissions]="model.customerPermissions"></customer-permissions>

            <p *ngIf="allowToChangeCustomerPermissions">
              <a uiSref="${STATE_REPORT}" [uiParams]="getReportPermissionsSREFParams" translate>APP.USER_PROFILE.PERMISSIONS.CHANGE_PERMISSIONS</a>
            </p>
          </div>

          <!-- Webshop Permissions -->
          <div *ngIf="isWebshopPermissionsView">
            <h4 class="form-title" translate>APP.USER_PROFILE.HEADINGS.WEBSHOP_PERMISSIONS</h4>

            <p *ngIf="loadingPermissions">
              <activity-indicator></activity-indicator>
              <span translate>APP.USER_PROFILE.PERMISSIONS.LOADING_PERMISSONS</span>
            </p>

            <div *ngIf="!loadingPermissions">
              <formly [form]="webshopPermissionsForm" [fields]="fields.webshopPermissionsFields" [model]="model.webshopPermissions"></formly>
              <dual-multi-select [possibleValues]="model.webshopPermissions.possibleWebshops" [(currentValues)]="model.webshopPermissions.selectedWebshops" (onChange)="onWebshopListChange()" [selectDisabled]="model.webshopPermissions.permissionToAllWebshops.id"></dual-multi-select>
            </div>

          </div>

        </div>
      </div>

    </div>
  `
})
export class UserPermissionsComponent implements IValidatingComponent, OnInit, AfterViewInit, OnDestroy {
  public fields:IFormFields;
  public model:IUserPermission = {};
  public userInfoForm:UntypedFormGroup = new UntypedFormGroup({});
  public webshopPermissionsForm:UntypedFormGroup = new UntypedFormGroup({});
  public loadingAccessSettings:boolean;
  public loadingPermissions:boolean;
  public isACSLUser:boolean;
  public isWebshopPermissionsView:boolean;
  public isCustomerUser:boolean;
  public userId:number;
  public allowToChangeCustomerPermissions:boolean;
  public allowToViewAccessSettings:boolean;
  public getReportPermissionsSREFParams:IObject;
  public registrationState:string;

  private genericUserFieldSelect:IExtraFieldConfigurationObject;
  private userProfile:IUserData;
  private eventHandler:Subscription;
  private loadDataPromise:Promise<void>;
  private wizard:GaWizard;
  private onValidationReadyDeferred:IGADeferred<void>;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private userProfileService:UserProfileService,
    private userProfileDataService:UserProfileDataService,
    private permissionsService:PermissionsService,
    private translationService:TranslationService,
    private wizardService:GaWizardService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.initWizardSetup();

    this.userProfileService.getUserProfilePromise()
      .then(this.initController);

    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private listen():void {
    this.eventHandler = this.gaEventService.events?.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_USER_PROFILE_UPDATED) {
        this.initController(event.data);
      }
    });
  }

  private initController = (userData:IUserData) => {
    this.userProfile = userData;
    this.registrationState = userData.registrationState;
    this.fields = this.createFields();
    this.isACSLUser = this.userProfileService.isACSLUser();
    this.model.userInfo = this.userProfileService.getUserInfo();
    this.userId = this.model.userInfo?.userId;
    this.isWebshopPermissionsView = this.userProfileService.isWebshopPermissionsView();
    this.isCustomerUser = this.userProfileService.isCustomerUser();
    this.allowToChangeCustomerPermissions = this.userProfileService.manageExternalUser() || this.authService.checkAcslUser();
    this.allowToViewAccessSettings = this.userProfileService.manageExternalUser() && this.authService.checkAcslUser();

    // link to the reports with customer permissions
    this.getReportPermissionsSREFParams = {
      [ReportParameterCode.REPORT_ID]: 31,
      [ReportParameterShortCode.AUTO_LOAD]: true,
      [ReportParameterShortCode.FILTERS]: [
        `Deusrnm\t${this.userProfile.login}`
      ]
    };

    this.loadData();
  };

  private initWizardSetup():void {
    this.wizard = this.wizardService.getInstance(USERPROFILE_WIZARD);

    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, UserProfileStepIds.USER_PERMISSIONS);
  }

  private loadData = ():Promise<any> => {
    return this.loadDataPromise = new Promise((resolve, reject) => {

      this.model.userInfo = this.userProfileService.getUserInfo();

      // fill generic user list
      if (this.genericUserFieldSelect) {
        this.formlyService.fillField(this.genericUserFieldSelect, this.genericUserPromise());
      }

      if (!this.model.userInfo.userId) { //we can't know the permissions yet
        resolve();
      } else {
        // clear permissions
        this.model.acslPermissions = undefined;
        this.model.customerPermissions = undefined;
        this.model.webshopPermissions = undefined;

        // loading permissions
        this.loadingPermissions = true;
        this.userProfileService.loadPermissions()
          .then((permissions:IWebshopPermissionResponse | IOrderQuotePermissionResponse) => {
            // access settings
            if (permissions.userType) {
              this.isCustomerUser = this.userProfileService.isCustomerUser();
              this.loadAccessSettings();
            }


            if (this.isCustomerUser) {
              // Permissions for CUSTOMER users
              this.model.customerPermissions = this.permissionsService.getCustomerPermissions(permissions);
            } else if (this.isACSLUser) {
              // Permissions for ACSL users
              if ((<IOrderQuotePermissionResponse>permissions).permissionTypes) {
                this.model.acslPermissions = {
                  order: _.find((<IOrderQuotePermissionResponse>permissions).permissionTypes, (permissionType:IPermissionTypeACSL) => permissionType.workflowType === UserProfileService.TYPE_PO),
                  quote: _.find((<IOrderQuotePermissionResponse>permissions).permissionTypes, (permissionType:IPermissionTypeACSL) => permissionType.workflowType === UserProfileService.TYPE_PQ)
                };
              }
            } else if (this.isWebshopPermissionsView) {
              // Permissions for others: webshop permissions view
              this.model.webshopPermissions = {
                userType: permissions.userType,
                permissionToAllWebshops: {
                  id: (<IWebshopPermissionResponse>permissions).permissionToAllWebshops
                },
                selectedWebshops: (<IWebshopPermissionResponse>permissions).selectedWebshops || [],
                possibleWebshops: (<IWebshopPermissionResponse>permissions).possibleWebshops || []
              };

              this.userProfileService.updatePermissions({
                webshopPermissions: this.model.webshopPermissions
              });
            }

          })
          .then(resolve)
          .catch(reject)
          .finally(() => this.loadingPermissions = false);
      }

      this.loadAccessSettings();
    });
  };

  private createFields():IFormFields {
    const labelClass:string = 'col-md-2 col-sm-3';
    const valueClass:string = 'col-md-5 col-sm-7';

    const fields:IFormFields = {
      userInfoFields: [],
      webshopPermissionsFields: []
    };

    // user permissions
    fields.userInfoFields.push(
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.USER_PERMISSIONS')
        .build()
    );

    if (this.userProfileService.manageExternalUser()) {
      this.genericUserFieldSelect = this.formlyService.aSelect('genericUser', 'APP.USER_PROFILE.PERMISSIONS.GENERIC_USER')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(() => {
          this.userProfileService.updateGenericUser(this.model.userInfo.genericUser);
          if (this.isCustomerUser) {
            this.loadAccessSettings();
          }
        })
        .build();
      fields.userInfoFields.push(this.genericUserFieldSelect);
    } else {
      fields.userInfoFields.push(
        this.formlyService.aValue('genericUser', 'APP.USER_PROFILE.PERMISSIONS.GENERIC_USER')
          .withFormat(this.formattingService.formatLov)
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .build()
      );
    }

    fields.userInfoFields.push(
      this.formlyService.aValue('userGroup', 'APP.USER_PROFILE.PERMISSIONS.USER_GROUP')
        .withFormat(this.formattingService.formatName)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo(this.userProfile.userGroup?.description)
        .showInfo()
        .build()
    );

    const options:IWebshopPermissionSelector[] = [
      {
        description: this.translationService.instant('APP.USER_PROFILE.PERMISSIONS.RADIO_LABEL_ALL_WEBSHOPS'),
        id: true
      },
      {
        description: this.translationService.instant('APP.USER_PROFILE.PERMISSIONS.RADIO_LABEL_SELECTED_WEBSHOPS'),
        id: false
      }
    ];
    fields.webshopPermissionsFields.push(
      this.formlyService.aRadio('permissionToAllWebshops')
        .withFormat(this.formattingService.formatLov)
        .withoutWrapper()
        .isRequired()
        .withOptions(options)
        .onChange(() => this.updatePermissions())
        .build()
    );

    return fields;
  }

  public onWebshopListChange():void {
    // When all webshops are selected in the dual multi select component, force the radio selector on "All webshops"
    if (this.model.webshopPermissions.possibleWebshops) {
      if (!this.model.webshopPermissions.possibleWebshops.length) {
        this.model.webshopPermissions.permissionToAllWebshops = { id: true };

        _.each(this.model.webshopPermissions.selectedWebshops, (selectedWebshop:IDualMultiSelectElement) => {
          this.model.webshopPermissions.possibleWebshops.push(selectedWebshop);
        });
        this.model.webshopPermissions.selectedWebshops = [];
      }
    }

    this.updatePermissions();
  }

  private updatePermissions():void {
    this.userProfileService.updatePermissions({
      webshopPermissions: this.model.webshopPermissions
    });
  }

  public addEmailSuffix(accessSettings:IAccessSettings):void {
    accessSettings.buttons.addEmailSuffixBusy = true;
    accessSettings.buttons.addEmailSuffixDisabled = true;
    this.userProfileDataService.addEmailSuffix(this.userProfile.email, accessSettings.companyId, accessSettings.webshopId)
      .then((response:IWebshop | ICompany) => {
        this.messageService.showFeedbackMessage('The email suffix has been added');
        if (response?.emailSuffix) {
          accessSettings.emailSuffix = response.emailSuffix;
        }
      })
      .catch(() => {
        accessSettings.buttons.addEmailSuffixDisabled = false;
      })
      .finally(() => {
        accessSettings.buttons.addEmailSuffixBusy = false;
      });
  }

  public sendReminderToApprover(accessSettings:IAccessSettings):void {
    accessSettings.buttons.sendReminderBusy = true;
    accessSettings.buttons.sendReminderDisabled = true;
    const countryId:number = this.model.userInfo.officeCountry?.id;
    this.userProfileDataService.sendApprovalReminder(this.userId, accessSettings.webshopId, accessSettings.companyId, countryId)
      .then(() => {
        this.messageService.showFeedbackMessage(`A reminder has been sent to the approver ${this.formattingService.formatContact(accessSettings.approver)}`);
      })
      .catch(() => {
        accessSettings.buttons.sendReminderDisabled = false;
      })
      .finally(() => {
        accessSettings.buttons.sendReminderBusy = false;
      });
  }

  public loadAccessSettings = () => {
    if (this.allowToViewAccessSettings) {
      const companyId:number = this.model.userInfo?.company?.id;
      const email:string = this.model.userInfo.email;
      const genericUserId:number = this.isCustomerUser? this.model.userInfo.genericUser?.id : undefined;
      const countryId:number = this.model.userInfo.officeCountry?.id;

      if (companyId && email) {
        this.loadingAccessSettings = true;
        this.userProfileDataService.getAccessSettings(companyId, email, genericUserId, countryId)
          .then((accessSettings:IAccessSettingsResponse) => {
            this.model.accessSettings = this.formatAccessSettings(accessSettings);
          })
          .finally(() => this.loadingAccessSettings = false);
      }
    }
  };

  // these 2 methods are only executed from the controller userprofile.controller.ts method saveProfile()
  //noinspection JSUnusedGlobalSymbols
  public beforeValidate = ():Promise<void> => {
    return this.onValidationReadyDeferred.promise
      .then(() => Promise.all([
        this.loadDataPromise,
        this.userProfileService.getGenericUsersPromise()
      ])
      .then(() => {
        return;
      }));
  };

  //noinspection JSUnusedGlobalSymbols
  public validate = (context:IValidationContext):Promise<string[]> => {
    const errorMessages = [];
    // always check that there is at least one webshop selected when 'selected webshops' is chosen
    const wsPermissions:IWebshopPermission = this.model.webshopPermissions;
    if (wsPermissions?.permissionToAllWebshops?.id === false && wsPermissions.selectedWebshops && !wsPermissions.selectedWebshops.length) {
      errorMessages.push(this.translationService.instant('APP.USER_PROFILE.PERMISSIONS.NO_WEBSHOP_SELECTED'));
    }

    //when there is a generic user, set all webshops to true if user hasn't made a selection
    if (this.model.userInfo?.genericUser && wsPermissions?.permissionToAllWebshops && util.isNullOrUndefined(wsPermissions.permissionToAllWebshops.id)) {
      wsPermissions.permissionToAllWebshops.id = true;
      this.updatePermissions();
    }

    // when user tries to validate, we check that there is a generic user, and that one of the webshop radio buttons is selected
    if (context.type === 'APPROVE') {
      if (!this.model.userInfo.genericUser) {
        errorMessages.push(this.translationService.instant('GA.FORM.REQUIRED', { label: this.translationService.instant('APP.USER_PROFILE.PERMISSIONS.GENERIC_USER') }));
      }

      if (this.isWebshopPermissionsView && wsPermissions?.permissionToAllWebshops && util.isNullOrUndefined(wsPermissions.permissionToAllWebshops.id)) {
        errorMessages.push(this.translationService.instant('APP.USER_PROFILE.PERMISSIONS.NO_WEBSHOP_PERMISSION_SELECTED'));
      }
    }

    return Promise.resolve(errorMessages);
  };

  private formatAccessSettings(accessSettingsData:IAccessSettingsResponse):IAccessSettings[] {
    const accessSettings:IAccessSettings[] = [];

    const selector:IWebshopAccessSettings[] | ICompanyAccessSettings[] = this.isCustomerUser? accessSettingsData.webshopAccessSettings : accessSettingsData.companyAccessSettings && [ accessSettingsData.companyAccessSettings ];

    _.each(selector, (settings:IWebshopAccessSettings | ICompanyAccessSettings) => {
      const type:string = this.isCustomerUser? 'webshop' : 'company';

      if (settings[ type ]) {
        const as:IAccessSettings = {
          name: settings[ type ].name,
          url: '',
          emailSuffix: this.formatEmailSuffix(settings[ type ].emailSuffix),
          userApproval: settings[ type ].userApproval,
          approver: settings.approver,
          formattedApprover: this.formattingService.formatContact(settings.approver),
          buttons: {
            addEmailSuffixDisabled: !settings.allowAddEmailSuffix
          }
        };

        if (this.isCustomerUser) {
          as.webshopId = settings[ type ].id;
          as.isMainWebshop = settings[ type ].isMainWebshop;
          as.url = URL_WEBSHOP_ACCESS_SETTINGS.replace(/{webshopId}/gi, settings[type].id);
        } else {
          as.companyId = settings[ type ].id;
          as.url = `/ga2/#/unacc?url=%2Funacc%2FCompanyNewView_Edit.jsp%3Frow0_Decomid%3D${settings[ type ].id}%26ejbEvent%3Dedit`;
        }
        accessSettings.push(as);
      }
    });

    return accessSettings;
  }

  private formatEmailSuffix(emailSuffixes:string) {
    return  emailSuffixes?.split(',').join(', ');
  }

  private genericUserPromise = ():Promise<IGenericUser[]> => {
    let genericUserPromise:Promise<IGenericUser[]> = this.userProfileService.getGenericUsersPromise();
    if(!genericUserPromise) {
      genericUserPromise = Promise.resolve(this.userProfileService.getGenericUsers());
    }
    return genericUserPromise;
  };

  public ngOnDestroy(): void {
    this.wizard.clearCurrentValidator();
    this.eventHandler.unsubscribe();
  }
}